<template>
  <div class="editor-toolbar" :class="{ 'editor-toolbar--compact': compact }">
    <div class="editor-toolbar__group">
      <!-- Bold -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('bold') }"
        title="Bold (Ctrl+B)"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <v-icon size="18">mdi-format-bold</v-icon>
      </button>

      <!-- Italic -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('italic') }"
        title="Italic (Ctrl+I)"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <v-icon size="18">mdi-format-italic</v-icon>
      </button>

      <!-- Underline -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('underline') }"
        title="Underline (Ctrl+U)"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <v-icon size="18">mdi-format-underline</v-icon>
      </button>

      <!-- Strikethrough -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('strike') }"
        title="Strikethrough"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <v-icon size="18">mdi-format-strikethrough</v-icon>
      </button>
    </div>

    <div class="editor-toolbar__separator" />

    <div class="editor-toolbar__group">
      <!-- Bullet List -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('bulletList') }"
        title="Bullet List"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <v-icon size="18">mdi-format-list-bulleted</v-icon>
      </button>

      <!-- Numbered List -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('orderedList') }"
        title="Numbered List"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <v-icon size="18">mdi-format-list-numbered</v-icon>
      </button>

      <!-- Blockquote -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('blockquote') }"
        title="Quote"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >
        <v-icon size="18">mdi-format-quote-close</v-icon>
      </button>
    </div>

    <div class="editor-toolbar__separator" />

    <div class="editor-toolbar__group">
      <!-- Link -->
      <button
        type="button"
        class="editor-toolbar__btn"
        :class="{ 'editor-toolbar__btn--active': editor.isActive('link') }"
        title="Insert Link"
        @click="toggleLink"
      >
        <v-icon size="18">mdi-link</v-icon>
      </button>
    </div>

    <!-- Link Input Dialog -->
    <v-dialog v-model="showLinkDialog" max-width="400" :z-index="5000">
      <v-card>
        <v-card-title class="text-h6">Insert Link</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="linkUrl"
            label="URL"
            placeholder="https://example.com"
            variant="outlined"
            density="compact"
            autofocus
            @keydown.enter="insertLink"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showLinkDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="insertLink">Insert</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import type { Editor } from "@tiptap/vue-3";

const props = defineProps<{
  editor: Editor;
  compact?: boolean;
}>();

const showLinkDialog = ref(false);
const linkUrl = ref("");

function toggleLink() {
  if (props.editor.isActive("link")) {
    // Remove link
    props.editor.chain().focus().unsetLink().run();
  } else {
    // Show dialog to add link
    const previousUrl = props.editor.getAttributes("link").href;
    linkUrl.value = previousUrl || "";
    showLinkDialog.value = true;
  }
}

function insertLink() {
  if (linkUrl.value) {
    // Ensure URL has protocol
    let url = linkUrl.value.trim();
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
      url = "https://" + url;
    }

    props.editor.chain().focus().setLink({ href: url }).run();
  }
  showLinkDialog.value = false;
  linkUrl.value = "";
}
</script>

<style scoped lang="scss">
.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  flex-wrap: wrap;

  &--compact {
    padding: 6px 8px;

    .editor-toolbar__btn {
      min-width: 36px;
      min-height: 36px;
      padding: 6px;
    }
  }

  &__group {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__separator {
    width: 1px;
    height: 20px;
    background: rgba(var(--v-theme-on-surface), 0.12);
    margin: 0 4px;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
    padding: 8px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), 0.7);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }

    &--active {
      background: rgba(var(--v-theme-primary), 0.15);
      color: rgb(var(--v-theme-primary));
    }

    // Touch-friendly size
    @media (pointer: coarse) {
      min-width: 44px;
      min-height: 44px;
    }
  }
}
</style>
